import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, CalendarDays, CheckCircle2, GraduationCap, Loader2, RefreshCw, Save, ShieldAlert, Users } from "lucide-react";
import { getApiUrl } from "../../config/api";

type AcademicYear = {
  id: number;
  name: string;
  startYear: number;
  endYear: number | null;
  startsOn: string | null;
  endsOn: string | null;
  status: string;
  isCurrent: number;
  activeTerm?: string | null;
};

type SchoolClass = {
  id: number;
  name: string;
};

type ProgressionRule = {
  sourceClassId: number;
  targetClassId: number | null;
  action: string;
  notes?: string | null;
};

type PreviewRow = {
  studentId: number;
  studentName: string;
  sourceClassId: number;
  sourceClassName: string;
  targetClassId: number | null;
  targetClassName: string | null;
  decision: string;
  learnerStatus: string;
  notes?: string | null;
};

type PreviewSummary = {
  totalLearners: number;
  promoted: number;
  retained: number;
  graduated: number;
  unresolved: number;
};

type PromotionConfigResponse = {
  tenantId: number;
  currentAcademicYear: AcademicYear | null;
  academicYears: AcademicYear[];
  classes: SchoolClass[];
  progressionRules: ProgressionRule[];
  recentBatches: Array<{
    id: number;
    sourceAcademicYearId: number;
    targetAcademicYearId: number;
    promotedCount: number;
    retainedCount: number;
    completedCount: number;
    executedAt: string;
  }>;
};

type RuleDraft = {
  mode: "auto" | "promote" | "retain" | "graduate";
  targetClassId: string;
  notes: string;
};

const emptyRuleDraft: RuleDraft = {
  mode: "auto",
  targetClassId: "",
  notes: ""
};

const LearnerPromotion: React.FC = () => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [savingRules, setSavingRules] = useState(false);
  const [creatingYear, setCreatingYear] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [config, setConfig] = useState<PromotionConfigResponse | null>(null);
  const [targetAcademicYearId, setTargetAcademicYearId] = useState("");
  const [ruleDrafts, setRuleDrafts] = useState<Record<number, RuleDraft>>({});
  const [retainedStudentIds, setRetainedStudentIds] = useState<number[]>([]);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [previewSummary, setPreviewSummary] = useState<PreviewSummary | null>(null);
  const [unresolvedRows, setUnresolvedRows] = useState<Array<{ studentId: number; studentName: string; reason: string }>>([]);

  const [newAcademicYear, setNewAcademicYear] = useState({
    startYear: String(new Date().getFullYear() + 1),
    endYear: String(new Date().getFullYear() + 1),
    startsOn: `${new Date().getFullYear() + 1}-01-01`,
    endsOn: `${new Date().getFullYear() + 1}-12-31`
  });

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    window.setTimeout(() => setMessage(null), 5000);
  };

  const buildRulePayload = () => {
    return Object.entries(ruleDrafts)
      .filter(([, value]) => value.mode !== "auto")
      .map(([sourceClassId, value]) => ({
        sourceClassId: Number(sourceClassId),
        targetClassId: value.mode === "promote" ? Number(value.targetClassId) : null,
        action: value.mode,
        notes: value.notes.trim() || null
      }));
  };

  const applyConfigToDrafts = (nextConfig: PromotionConfigResponse) => {
    const nextDrafts: Record<number, RuleDraft> = {};
    nextConfig.classes.forEach((schoolClass) => {
      const existingRule = nextConfig.progressionRules.find((rule) => Number(rule.sourceClassId) === Number(schoolClass.id));
      if (existingRule) {
        nextDrafts[schoolClass.id] = {
          mode: existingRule.action === "promote"
            ? "promote"
            : existingRule.action === "retain"
              ? "retain"
              : "graduate",
          targetClassId: existingRule.targetClassId ? String(existingRule.targetClassId) : "",
          notes: existingRule.notes || ""
        };
      } else {
        nextDrafts[schoolClass.id] = { ...emptyRuleDraft };
      }
    });
    setRuleDrafts(nextDrafts);
  };

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get<PromotionConfigResponse>(getApiUrl("academics/promotion/config"), { headers });
      const nextConfig = response.data;
      setConfig(nextConfig);
      applyConfigToDrafts(nextConfig);

      const firstPlannedYear = nextConfig.academicYears.find((year) => !Number(year.isCurrent));
      setTargetAcademicYearId(firstPlannedYear ? String(firstPlannedYear.id) : "");
    } catch (err: any) {
      showMessage("error", err.response?.data?.message || "Failed to load academic promotion settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadConfig();
  }, [token]);

  const handleRuleChange = (classId: number, patch: Partial<RuleDraft>) => {
    setRuleDrafts((prev) => ({
      ...prev,
      [classId]: {
        ...(prev[classId] || emptyRuleDraft),
        ...patch
      }
    }));
  };

  const handleSaveRules = async () => {
    setSavingRules(true);
    try {
      const response = await axios.put(
        getApiUrl("academics/promotion/progression-rules"),
        { rules: buildRulePayload() },
        { headers }
      );
      showMessage("success", response.data?.message || "Progression rules saved.");
      await loadConfig();
    } catch (err: any) {
      showMessage("error", err.response?.data?.message || "Failed to save progression rules.");
    } finally {
      setSavingRules(false);
    }
  };

  const handleCreateAcademicYear = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingYear(true);
    try {
      const response = await axios.post(
        getApiUrl("academics/promotion/academic-years"),
        {
          startYear: Number(newAcademicYear.startYear),
          endYear: Number(newAcademicYear.endYear),
          startsOn: newAcademicYear.startsOn,
          endsOn: newAcademicYear.endsOn
        },
        { headers }
      );
      showMessage("success", response.data?.message || "Academic year created.");
      await loadConfig();
    } catch (err: any) {
      showMessage("error", err.response?.data?.message || "Failed to create academic year.");
    } finally {
      setCreatingYear(false);
    }
  };

  const handleGeneratePreview = async () => {
    if (!targetAcademicYearId) {
      showMessage("error", "Select the target academic year first.");
      return;
    }

    setPreviewing(true);
    try {
      const response = await axios.post(
        getApiUrl("academics/promotion/preview"),
        {
          targetAcademicYearId: Number(targetAcademicYearId),
          rules: buildRulePayload(),
          retainedStudentIds
        },
        { headers }
      );
      setPreviewRows(response.data?.preview || []);
      setPreviewSummary(response.data?.summary || null);
      setUnresolvedRows(response.data?.unresolved || []);
      if ((response.data?.summary?.unresolved || 0) === 0) {
        showMessage("success", "Promotion preview generated successfully.");
      }
    } catch (err: any) {
      showMessage("error", err.response?.data?.message || "Failed to generate promotion preview.");
    } finally {
      setPreviewing(false);
    }
  };

  const toggleRetained = (studentId: number) => {
    setRetainedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const handleApplyPromotion = async () => {
    if (!targetAcademicYearId) {
      showMessage("error", "Select the target academic year first.");
      return;
    }
    if (!previewRows.length && !unresolvedRows.length) {
      showMessage("error", "Generate a preview before applying promotion.");
      return;
    }
    if (unresolvedRows.length > 0) {
      showMessage("error", "Resolve all unmapped learners before applying promotion.");
      return;
    }
    if (!window.confirm("Apply this academic rollover? This will move learners into the selected academic year and update live class assignments.")) {
      return;
    }

    setApplying(true);
    try {
      const response = await axios.post(
        getApiUrl("academics/promotion/apply"),
        {
          targetAcademicYearId: Number(targetAcademicYearId),
          rules: buildRulePayload(),
          retainedStudentIds
        },
        { headers }
      );
      showMessage("success", response.data?.message || "Learner promotion completed.");
      setRetainedStudentIds([]);
      setPreviewRows([]);
      setPreviewSummary(null);
      setUnresolvedRows([]);
      await loadConfig();
    } catch (err: any) {
      showMessage("error", err.response?.data?.message || "Failed to apply learner promotion.");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="animate-spin" size={20} />
          <span>Loading academic promotion workspace...</span>
        </div>
      </div>
    );
  }

  const classes = config?.classes || [];
  const academicYears = config?.academicYears || [];
  const currentAcademicYear = config?.currentAcademicYear || null;

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.35em] text-orange-500">Academic Promotion</p>
              <h1 className="mt-2 text-3xl font-black text-slate-900">Learner Promotion & Year Rollover</h1>
              <p className="mt-2 max-w-3xl text-sm text-slate-500">
                Configure class progression, create academic years, preview the rollover, retain selected learners, and safely promote the rest.
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              <p className="font-semibold">Current Academic Year</p>
              <p>{currentAcademicYear?.name || "Not configured"}</p>
            </div>
          </div>
          {message && (
            <div className={`mt-5 rounded-2xl border px-4 py-3 text-sm ${
              message.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-rose-200 bg-rose-50 text-rose-700"
            }`}>
              {message.text}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-black text-slate-900">Academic Year Setup</h2>
                <p className="mt-1 text-sm text-slate-500">Choose the target year for promotion or create the next one now.</p>
              </div>
              <CalendarDays className="text-orange-500" size={22} />
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="rounded-2xl border border-slate-200 p-4">
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Target Academic Year</span>
                <select
                  className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none focus:border-orange-400"
                  value={targetAcademicYearId}
                  onChange={(e) => setTargetAcademicYearId(e.target.value)}
                >
                  <option value="">Select target year</option>
                  {academicYears
                    .filter((year) => !Number(year.isCurrent))
                    .map((year) => (
                      <option key={year.id} value={year.id}>
                        {year.name} ({year.status})
                      </option>
                    ))}
                </select>
              </label>

              <form className="grid gap-3 rounded-2xl border border-slate-200 p-4" onSubmit={handleCreateAcademicYear}>
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Create Academic Year</span>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-400"
                    value={newAcademicYear.startYear}
                    onChange={(e) => setNewAcademicYear((prev) => ({ ...prev, startYear: e.target.value }))}
                    placeholder="Start year"
                  />
                  <input
                    className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-400"
                    value={newAcademicYear.endYear}
                    onChange={(e) => setNewAcademicYear((prev) => ({ ...prev, endYear: e.target.value }))}
                    placeholder="End year"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-400"
                    value={newAcademicYear.startsOn}
                    onChange={(e) => setNewAcademicYear((prev) => ({ ...prev, startsOn: e.target.value }))}
                  />
                  <input
                    type="date"
                    className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-400"
                    value={newAcademicYear.endsOn}
                    onChange={(e) => setNewAcademicYear((prev) => ({ ...prev, endsOn: e.target.value }))}
                  />
                </div>
                <button
                  type="submit"
                  disabled={creatingYear}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {creatingYear ? <Loader2 className="animate-spin" size={16} /> : <CalendarDays size={16} />}
                  Create Year
                </button>
              </form>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {academicYears.map((year) => (
                <div key={year.id} className={`rounded-2xl border p-4 ${Number(year.isCurrent) ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                  <p className="text-sm font-bold text-slate-900">{year.name}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.22em] text-slate-500">{year.status}</p>
                  <p className="mt-3 text-xs text-slate-500">{year.startsOn || "No start date"} to {year.endsOn || "No end date"}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-slate-900">Recent Promotion Runs</h2>
                <p className="mt-1 text-sm text-slate-500">Latest rollover batches for this school.</p>
              </div>
              <Users className="text-orange-500" size={22} />
            </div>
            <div className="mt-5 space-y-3">
              {(config?.recentBatches || []).length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 p-5 text-sm text-slate-500">No promotion batches have been run yet.</div>
              ) : (
                config?.recentBatches.map((batch) => (
                  <div key={batch.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-semibold text-slate-900">Batch #{batch.id}</p>
                    <p className="mt-1 text-xs text-slate-500">{new Date(batch.executedAt).toLocaleString()}</p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                      <span className="rounded-full bg-white px-3 py-1">Promoted: {batch.promotedCount}</span>
                      <span className="rounded-full bg-white px-3 py-1">Retained: {batch.retainedCount}</span>
                      <span className="rounded-full bg-white px-3 py-1">Completed: {batch.completedCount}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-black text-slate-900">Class Progression Rules</h2>
              <p className="mt-1 text-sm text-slate-500">Use manual mappings where automatic progression is not enough, especially for nursery or custom stream changes.</p>
            </div>
            <button
              type="button"
              onClick={handleSaveRules}
              disabled={savingRules}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {savingRules ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              Save Rules
            </button>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-y-3">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.25em] text-slate-400">
                  <th className="px-3">Current Class</th>
                  <th className="px-3">Rule</th>
                  <th className="px-3">Target Class</th>
                  <th className="px-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {classes.map((schoolClass) => {
                  const draft = ruleDrafts[schoolClass.id] || emptyRuleDraft;
                  return (
                    <tr key={schoolClass.id} className="rounded-2xl bg-slate-50 text-sm text-slate-700">
                      <td className="rounded-l-2xl px-3 py-3 font-semibold text-slate-900">{schoolClass.name}</td>
                      <td className="px-3 py-3">
                        <select
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 outline-none focus:border-orange-400"
                          value={draft.mode}
                          onChange={(e) => handleRuleChange(schoolClass.id, { mode: e.target.value as RuleDraft["mode"], targetClassId: e.target.value === "promote" ? draft.targetClassId : "" })}
                        >
                          <option value="auto">Automatic</option>
                          <option value="promote">Promote</option>
                          <option value="retain">Retain</option>
                          <option value="graduate">Graduate / Complete</option>
                        </select>
                      </td>
                      <td className="px-3 py-3">
                        <select
                          disabled={draft.mode !== "promote"}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 outline-none focus:border-orange-400 disabled:bg-slate-100 disabled:text-slate-400"
                          value={draft.targetClassId}
                          onChange={(e) => handleRuleChange(schoolClass.id, { targetClassId: e.target.value })}
                        >
                          <option value="">Select target class</option>
                          {classes
                            .filter((candidate) => candidate.id !== schoolClass.id)
                            .map((candidate) => (
                              <option key={candidate.id} value={candidate.id}>{candidate.name}</option>
                            ))}
                        </select>
                      </td>
                      <td className="rounded-r-2xl px-3 py-3">
                        <input
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 outline-none focus:border-orange-400"
                          value={draft.notes}
                          onChange={(e) => handleRuleChange(schoolClass.id, { notes: e.target.value })}
                          placeholder="Optional note"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-black text-slate-900">Promotion Preview</h2>
              <p className="mt-1 text-sm text-slate-500">Preview every learner before you commit the rollover. Use the retain toggle for repeaters and refresh the preview when needed.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleGeneratePreview}
                disabled={previewing}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-60"
              >
                {previewing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                Generate Preview
              </button>
              <button
                type="button"
                onClick={handleApplyPromotion}
                disabled={applying}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {applying ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                Apply Promotion
              </button>
            </div>
          </div>

          {previewSummary && (
            <div className="mt-5 grid gap-4 md:grid-cols-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Learners</p>
                <p className="mt-2 text-2xl font-black text-slate-900">{previewSummary.totalLearners}</p>
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-emerald-700">Promoted</p>
                <p className="mt-2 text-2xl font-black text-emerald-900">{previewSummary.promoted}</p>
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-amber-700">Retained</p>
                <p className="mt-2 text-2xl font-black text-amber-900">{previewSummary.retained}</p>
              </div>
              <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-sky-700">Completed</p>
                <p className="mt-2 text-2xl font-black text-sky-900">{previewSummary.graduated}</p>
              </div>
            </div>
          )}

          {unresolvedRows.length > 0 && (
            <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-5">
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 text-rose-600" size={18} />
                <div>
                  <p className="font-semibold text-rose-800">Some learners cannot be promoted yet</p>
                  <p className="mt-1 text-sm text-rose-700">Add or adjust progression rules for the affected classes, then refresh the preview.</p>
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-[0.25em] text-rose-500">
                      <th className="py-2 pr-4">Learner</th>
                      <th className="py-2 pr-4">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unresolvedRows.map((row) => (
                      <tr key={row.studentId} className="border-t border-rose-100">
                        <td className="py-3 pr-4 font-medium text-rose-900">{row.studentName}</td>
                        <td className="py-3 pr-4 text-rose-700">{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-y-3">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.25em] text-slate-400">
                  <th className="px-3">Retain</th>
                  <th className="px-3">Learner</th>
                  <th className="px-3">Current Class</th>
                  <th className="px-3">Target</th>
                  <th className="px-3">Decision</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500">
                      Generate a preview to inspect learner promotion outcomes.
                    </td>
                  </tr>
                ) : (
                  previewRows.map((row) => (
                    <tr key={row.studentId} className="rounded-2xl bg-slate-50 text-sm text-slate-700">
                      <td className="rounded-l-2xl px-3 py-3">
                        <input
                          type="checkbox"
                          checked={retainedStudentIds.includes(row.studentId)}
                          onChange={() => toggleRetained(row.studentId)}
                          className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                        />
                      </td>
                      <td className="px-3 py-3 font-semibold text-slate-900">{row.studentName}</td>
                      <td className="px-3 py-3">{row.sourceClassName}</td>
                      <td className="px-3 py-3">
                        {row.targetClassName ? (
                          <span className="inline-flex items-center gap-2">
                            <span>{row.targetClassName}</span>
                            <ArrowRight size={14} className="text-slate-400" />
                            <span className="text-xs text-slate-500">{academicYears.find((year) => String(year.id) === targetAcademicYearId)?.name || "Target year"}</span>
                          </span>
                        ) : (
                          <span className="text-slate-500">No class placement</span>
                        )}
                      </td>
                      <td className="rounded-r-2xl px-3 py-3">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                          row.decision === "promoted"
                            ? "bg-emerald-100 text-emerald-700"
                            : row.decision === "retained"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-sky-100 text-sky-700"
                        }`}>
                          {row.decision === "graduated" ? "Completed" : row.decision}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LearnerPromotion;
